import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Landmark } from 'lucide-react';
import type { PlatformClearing } from '@worki/shared';
import { api } from '../../lib/api.js';
import { currentLocale } from '../../i18n.js';
import { formatDateTime } from '../../lib/format.js';
import { EmptyState, Spinner } from '../../components/ui.js';
import { AdminCard, Pill, Row, Stat } from './parts.js';

/**
 * ADM-013 — the clearing house, from the platform's side.
 *
 * Not a second billing screen: the customer's own is about their plan, and this
 * one is about the arrangement behind every plan. It answers the question that
 * has no other answer anywhere — is the provider still talking to us? A
 * provider that quietly stops calling looks exactly like a quiet week, and the
 * first sign otherwise is a customer whose card was charged for a subscription
 * this system thinks expired.
 */
export function ClearingSection() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();

  const clearing = useQuery({
    queryKey: ['platform', 'clearing'],
    queryFn: () => api.get<PlatformClearing>('/platform/clearing'),
  });

  if (clearing.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const data = clearing.data;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <AdminCard title={t('admin.clearing.provider')} hint={t('admin.clearing.providerHint')}>
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Landmark className="h-4 w-4 text-primary" aria-hidden />
            <strong className="text-sm" dir="ltr">
              {data.driver}
            </strong>
            {/* `off` is a configuration, not a fault — a platform that invoices
                by hand is a platform, so it is stated rather than warned about. */}
            <Pill tone={data.driver === 'off' ? 'neutral' : 'good'}>
              {t(data.driver === 'off' ? 'admin.clearing.off' : 'admin.clearing.live')}
            </Pill>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">{t('admin.clearing.webhookUrl')}</p>
            <p className="text-xs break-all" dir="ltr">
              {data.webhookUrl}
            </p>
          </div>
        </div>
      </AdminCard>

      <AdminCard>
        <div className="grid grid-cols-3 gap-2">
          <Stat label={t('admin.clearing.paying')} value={data.paying} tone="good" />
          <Stat label={t('admin.clearing.pastDue')} value={data.pastDue} tone={data.pastDue > 0 ? 'bad' : 'muted'} />
          {/* Events naming an account we do not hold. Zero is the only healthy
              number, so it is on the screen rather than in a log nobody opens. */}
          <Stat
            label={t('admin.clearing.unattributed')}
            value={data.unattributed}
            tone={data.unattributed > 0 ? 'bad' : 'muted'}
          />
        </div>
      </AdminCard>

      <AdminCard title={t('admin.clearing.events')} hint={t('admin.clearing.eventsHint')}>
        {data.events.length === 0 ? (
          <EmptyState icon={<CreditCard className="h-7 w-7" />} title={t('admin.clearing.noEvents')} />
        ) : (
          <div className="space-y-2">
            {data.events.map((event) => (
              <Row
                key={event.id}
                icon={<CreditCard className="h-4 w-4" aria-hidden />}
                title={t(`platform.billingEvent.${event.type}`, { defaultValue: event.type })}
                subtitle={event.businessName ?? t('admin.clearing.unknownAccount')}
                meta={
                  <span className="flex items-center gap-2">
                    {/* Recorded but never applied: the provider spoke and the
                        subscription did not move, which is the shape of a
                        webhook we could not act on. */}
                    {event.appliedAt === null && <Pill tone="warn">{t('admin.clearing.notApplied')}</Pill>}
                    <span className="text-[11px] text-muted-foreground ltr-nums">
                      {formatDateTime(event.at, locale)}
                    </span>
                  </span>
                }
                {...(event.businessId
                  ? { onClick: () => navigate(`/platform/businesses?open=${event.businessId ?? ''}`) }
                  : {})}
              />
            ))}
          </div>
        )}
      </AdminCard>
    </div>
  );
}
